import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService, AuthUser, AuthStatus } from '../services/authService';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    try {
      console.log('🔍 Checking authentication status...');
      console.log('🔍 Current session ID in AuthService:', authService['sessionId']);
      const status = await authService.checkAuthStatus();
      console.log('📊 Auth status result:', status);
      setUser(status.user);
      setIsAuthenticated(status.authenticated);
      console.log('✅ Authentication state updated:', { user: status.user, isAuthenticated: status.authenticated });
    } catch (error) {
      console.error('❌ Error checking auth status:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async () => {
    setIsLoading(true);
    try {
      await authService.startSSO();
      
      // Listen for auth success
      const handleAuthSuccess = (event: CustomEvent) => {
        window.removeEventListener('auth:success', handleAuthSuccess as EventListener);
        window.removeEventListener('auth:timeout', handleAuthTimeout as EventListener);
        
        if (event.detail?.user) {
          setUser(event.detail.user);
          setIsAuthenticated(true);
        }
        setIsLoading(false);
      };

      const handleAuthTimeout = () => {
        window.removeEventListener('auth:success', handleAuthSuccess as EventListener);
        window.removeEventListener('auth:timeout', handleAuthTimeout as EventListener);
        setIsLoading(false);
      };

      window.addEventListener('auth:success', handleAuthSuccess as EventListener);
      window.addEventListener('auth:timeout', handleAuthTimeout as EventListener);

    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize auth on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if we have a session ID from a previous authentication
        const existingSessionId = localStorage.getItem('auth_session_id');
        if (existingSessionId) {
          // Set the session ID in the auth service
          authService.setSessionId(existingSessionId);
        }
        
        const status = await authService.init();
        setUser(status.user);
        setIsAuthenticated(status.authenticated);
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

          // Listen for auth events
      const handleAuthSuccess = (event: CustomEvent) => {
        console.log('🎉 Auth success event received:', event.detail);
        if (event.detail?.sessionId) {
          // Store the session ID and update AuthService
          localStorage.setItem('auth_session_id', event.detail.sessionId);
          authService.setSessionId(event.detail.sessionId);
          console.log('📝 Session ID stored and updated in AuthService, checking auth status...');
          checkAuth(); // This will update the authentication state
        } else if (event.detail?.user) {
          console.log('👤 User data received, setting authenticated');
          setUser(event.detail.user);
          setIsAuthenticated(true);
        }
      };

    const handleAuthLogout = () => {
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('auth:success', handleAuthSuccess as EventListener);
    window.addEventListener('auth:logout', handleAuthLogout);

    return () => {
      window.removeEventListener('auth:success', handleAuthSuccess as EventListener);
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

